import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import {
  Calendar, Clock, MapPin, Users, IndianRupee, Award,
  CheckCircle2, ArrowLeft, Share2, Download, User
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import axios from 'axios';
import { TrainingRegistrationForm } from '../components/TrainingRegistrationForm';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_URL = `${BACKEND_URL}/api`;
const SITE_URL = process.env.REACT_APP_SITE_URL || 'https://isde.academy';

const TrainingDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [training, setTraining] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isRegistrationOpen, setIsRegistrationOpen] = useState(false);

  useEffect(() => {
    fetchTrainingDetails();
  }, [slug]);

  const fetchTrainingDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`${API_URL}/public/training-calendar/slug/${slug}`);
      setTraining(response.data);
    } catch (err) {
      console.error('Error fetching training details:', err);
      setError(err.response?.status === 404 ? 'Training not found' : 'Failed to load training details');
    } finally {
      setLoading(false);
    }
  };

  const handleShare = async () => {
    const shareData = {
      title: training.meta_title || training.title,
      text: training.meta_description || training.description,
      url: window.location.href,
    };

    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (err) {
        console.log('Share cancelled or failed', err);
      }
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
    }
  };

  const handleRegister = () => {
    if (training.use_internal_form) {
      setIsRegistrationOpen(true);
    } else if (training.registration_link) {
      window.open(training.registration_link, '_blank');
    }
  };

  const handleRegistrationSuccess = () => {
    setIsRegistrationOpen(false);
    fetchTrainingDetails(); // Refresh to update seat count
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getStructuredData = () => {
    if (!training) return null;

    return {
      "@context": "https://schema.org",
      "@type": ["Event", "EducationEvent"],
      "name": training.title,
      "description": training.description,
      "startDate": training.date,
      "endDate": training.date,
      "eventAttendanceMode": training.mode === 'Online'
        ? "https://schema.org/OnlineEventAttendanceMode"
        : "https://schema.org/OfflineEventAttendanceMode",
      "eventStatus": "https://schema.org/EventScheduled",
      "location": training.mode === 'Online'
        ? {
            "@type": "VirtualLocation",
            "url": SITE_URL
          }
        : {
            "@type": "Place",
            "name": training.location,
            "address": {
              "@type": "PostalAddress",
              "addressLocality": training.location,
              "addressCountry": "IN"
            }
          },
      "image": training.og_image || training.poster_url,
      "organizer": {
        "@type": "Organization",
        "name": "ISDE Academy",
        "url": SITE_URL
      },
      "offers": {
        "@type": "Offer",
        "price": training.is_paid ? parseFloat(training.price.replace(/[^0-9.]/g, '')) : 0,
        "priceCurrency": "INR",
        "availability": training.seats_available > 0
          ? "https://schema.org/InStock"
          : "https://schema.org/SoldOut",
        "validFrom": new Date().toISOString()
      },
      "performer": training.instructor ? {
        "@type": "Person",
        "name": training.instructor
      } : undefined
    };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading training details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">{error}</h2>
          <p className="text-gray-600 mb-6">
            The training you're looking for could not be found. It may have been removed or the URL might be incorrect.
          </p>
          <div className="space-x-4">
            <Button onClick={() => navigate(-1)} variant="outline">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Go Back
            </Button>
            <Button onClick={() => navigate('/training-calendar')}>
              View All Trainings
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (!training) return null;

  const canonicalUrl = training.canonical_url || `${SITE_URL}/training-event/${training.slug}`;

  return (
    <>
      <Helmet>
        {/* Basic Meta Tags */}
        <title>{training.meta_title || `${training.title} | ISDE Academy`}</title>
        <meta name="description" content={training.meta_description || training.description} />
        {training.meta_keywords && <meta name="keywords" content={training.meta_keywords} />}
        <link rel="canonical" href={canonicalUrl} />

        {/* Open Graph Tags */}
        <meta property="og:type" content="event" />
        <meta property="og:title" content={training.meta_title || training.title} />
        <meta property="og:description" content={training.meta_description || training.description} />
        <meta property="og:url" content={canonicalUrl} />
        {(training.og_image || training.poster_url) && (
          <meta property="og:image" content={training.og_image || training.poster_url} />
        )}
        <meta property="og:site_name" content="ISDE Academy" />

        {/* Twitter Card Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={training.meta_title || training.title} />
        <meta name="twitter:description" content={training.meta_description || training.description} />
        {(training.og_image || training.poster_url) && (
          <meta name="twitter:image" content={training.og_image || training.poster_url} />
        )}

        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(getStructuredData())}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        {/* Breadcrumbs */}
        <div className="bg-white border-b">
          <div className="container mx-auto px-4 py-4">
            <nav className="flex items-center space-x-2 text-sm text-gray-600">
              <Link to="/" className="hover:text-blue-600">Home</Link>
              <span>/</span>
              <Link to="/training-calendar" className="hover:text-blue-600">Training Calendar</Link>
              {training.category && (
                <>
                  <span>/</span>
                  <Link
                    to={`/training-calendar?category=${training.category.id}`}
                    className="hover:text-blue-600"
                  >
                    {training.category.title}
                  </Link>
                </>
              )}
              <span>/</span>
              <span className="text-gray-900 font-medium truncate">{training.title}</span>
            </nav>
          </div>
        </div>

        {/* Hero Section */}
        <div className="bg-gradient-to-br from-blue-600 to-blue-800 text-white">
          <div className="container mx-auto px-4 py-12 lg:py-20">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              {/* Content */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
              >
                <Button
                  onClick={() => navigate(-1)}
                  variant="ghost"
                  className="text-white hover:bg-white/20 mb-6"
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back
                </Button>

                {training.is_featured && (
                  <Badge className="bg-yellow-500 text-yellow-900 mb-4">
                    <Award className="h-3 w-3 mr-1" />
                    Featured Training
                  </Badge>
                )}

                <h1 className="text-3xl lg:text-5xl font-bold mb-4">
                  {training.banner_heading || training.title}
                </h1>
                <p className="text-xl text-blue-100 mb-8 whitespace-pre-line">
                  {training.banner_description || training.description}
                </p>

                <div className="flex flex-wrap gap-4 mb-8">
                  <div className="flex items-center gap-2">
                    <Calendar className="h-5 w-5" />
                    <span>{formatDate(training.date)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="h-5 w-5" />
                    <span>{training.duration}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <MapPin className="h-5 w-5" />
                    <span>{training.location}</span>
                  </div>
                </div>

                <div className="flex flex-wrap gap-4">
                  <Button
                    size="lg"
                    onClick={handleRegister}
                    disabled={training.seats_available <= 0}
                    className="bg-white text-blue-600 hover:bg-gray-100"
                  >
                    {training.seats_available > 0 ? 'Register Now' : 'Fully Booked'}
                  </Button>
                  <Button
                    size="lg"
                    variant="outline"
                    onClick={handleShare}
                    className="border-white text-white hover:bg-white/20"
                  >
                    <Share2 className="h-4 w-4 mr-2" />
                    Share
                  </Button>
                </div>
              </motion.div>

              {/* Banner Thumbnail Image */}
              {(training.banner_thumbnail_url || training.poster_url) && (
                <motion.div
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6 }}
                  className="relative"
                >
                  <img
                    src={(training.banner_thumbnail_url || training.poster_url).startsWith('http')
                      ? (training.banner_thumbnail_url || training.poster_url)
                      : `${BACKEND_URL}${training.banner_thumbnail_url || training.poster_url}`}
                    alt={training.banner_heading || training.title}
                    className="rounded-lg shadow-2xl w-full h-auto"
                  />
                </motion.div>
              )}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="container mx-auto px-4 py-12">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Left Column - Details */}
            <div className="lg:col-span-2 space-y-8">
              {/* Overview Card */}
              <Card>
                <CardHeader>
                  <CardTitle>Training Overview</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                    {training.description}
                  </p>
                </CardContent>
              </Card>

              {/* Key Information */}
              <Card>
                <CardHeader>
                  <CardTitle>Key Information</CardTitle>
                </CardHeader>
                <CardContent className="grid md:grid-cols-2 gap-6">
                  <div className="flex items-start gap-3">
                    <Calendar className="h-6 w-6 text-blue-600 mt-1" />
                    <div>
                      <p className="font-semibold text-gray-900">Date</p>
                      <p className="text-gray-600">{formatDate(training.date)}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Clock className="h-6 w-6 text-blue-600 mt-1" />
                    <div>
                      <p className="font-semibold text-gray-900">Duration</p>
                      <p className="text-gray-600">{training.duration}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <MapPin className="h-6 w-6 text-blue-600 mt-1" />
                    <div>
                      <p className="font-semibold text-gray-900">Location</p>
                      <p className="text-gray-600">{training.location}</p>
                      <Badge variant="secondary" className="mt-1">{training.mode}</Badge>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Users className="h-6 w-6 text-blue-600 mt-1" />
                    <div>
                      <p className="font-semibold text-gray-900">Seats Available</p>
                      <p className={`text-lg font-bold ${training.seats_available > 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {training.seats_available > 0 ? `${training.seats_available} seats left` : 'Fully Booked'}
                      </p>
                    </div>
                  </div>
                  {training.instructor && (
                    <div className="flex items-start gap-3">
                      <User className="h-6 w-6 text-blue-600 mt-1" />
                      <div>
                        <p className="font-semibold text-gray-900">Instructor</p>
                        <p className="text-gray-600">{training.instructor}</p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Syllabus Download */}
              {training.syllabus_url && (
                <Card>
                  <CardHeader>
                    <CardTitle>Course Syllabus</CardTitle>
                    <CardDescription>Download the detailed course curriculum</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Button
                      variant="outline"
                      onClick={() => window.open(training.syllabus_url, '_blank')}
                      className="w-full sm:w-auto"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Download Syllabus
                    </Button>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Right Column - Sidebar */}
            <div className="space-y-6">
              {/* Pricing Card */}
              <Card className="sticky top-4">
                <CardHeader>
                  <CardTitle>Registration Details</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div>
                    <p className="text-sm text-gray-600 mb-2">Training Fee</p>
                    {training.is_paid ? (
                      <div className="flex items-baseline gap-2">
                        <IndianRupee className="h-6 w-6 text-blue-600" />
                        <span className="text-3xl font-bold text-gray-900">{training.price}</span>
                      </div>
                    ) : (
                      <span className="text-3xl font-bold text-green-600">Complimentary</span>
                    )}
                  </div>

                  <div className="space-y-3 pt-4 border-t">
                    <div className="flex items-center gap-2 text-green-600">
                      <CheckCircle2 className="h-5 w-5" />
                      <span className="text-sm">Certificate of completion</span>
                    </div>
                    <div className="flex items-center gap-2 text-green-600">
                      <CheckCircle2 className="h-5 w-5" />
                      <span className="text-sm">Course materials included</span>
                    </div>
                    <div className="flex items-center gap-2 text-green-600">
                      <CheckCircle2 className="h-5 w-5" />
                      <span className="text-sm">Expert instructor support</span>
                    </div>
                  </div>

                  <Button
                    onClick={handleRegister}
                    disabled={training.seats_available <= 0}
                    className="w-full"
                    size="lg"
                  >
                    {training.seats_available > 0 ? 'Register Now' : 'Fully Booked'}
                  </Button>

                  {training.seats_available > 0 && training.seats_available <= 5 && (
                    <p className="text-sm text-orange-600 text-center">
                      ⚠️ Only {training.seats_available} seats remaining!
                    </p>
                  )}
                </CardContent>
              </Card>

              {/* Category Card */}
              {training.category && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Training Category</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Link
                      to={`/training-calendar?category=${training.category.id}`}
                      className="text-blue-600 hover:text-blue-700 font-medium"
                    >
                      {training.category.title} →
                    </Link>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </div>

        {/* Registration Form Modal */}
        {training && (
          <TrainingRegistrationForm
            training={{
              id: training.id,
              title: training.title,
              date: training.date,
              location: training.location,
              price: training.price,
              is_paid: training.is_paid,
              seats_available: training.seats_available
            }}
            isOpen={isRegistrationOpen}
            onClose={() => setIsRegistrationOpen(false)}
            onSuccess={handleRegistrationSuccess}
          />
        )}
      </div>
    </>
  );
};

export default TrainingDetailPage;
